/**
 * Presentation for a single watchlist checkbox row in SelectListSheet.
 *
 * Displays checkbox, watchlist name, share/edit/delete action icons, and
 * player count. The checkbox half and the action-icon half are SIBLINGS, not
 * nested (SE-7739) — see the comment on the layout below.
 * @module features/scouting/components/SelectListRow/SelectListRowView
 */

import {
  CheckIcon,
  PencilIcon,
  Trash2Icon,
  UserPlusIcon,
} from "lucide-react-native";
import React, { memo } from "react";

import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from "@/components/atoms/Checkbox";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Props for SelectListRowView */
interface SelectListRowViewProps {
  /** Watchlist ID (used as checkbox value) */
  readonly id: string;
  /** Watchlist display name */
  readonly name: string;
  /** Number of players in this watchlist */
  readonly playerCount: number;
  /** Whether this watchlist is currently checked */
  readonly isChecked: boolean;
  /** Callback when this row is toggled */
  readonly onToggle: () => void;
  /** Callback when share icon is pressed (hidden when absent) */
  readonly onShare?: () => void;
  /** Callback when edit icon is pressed (hidden when absent) */
  readonly onEdit?: () => void;
  /** Callback when delete icon is pressed (hidden when absent) */
  readonly onDelete?: () => void;
}

/**
 * Renders a single watchlist checkbox row.
 * @param root0 - View props
 * @param root0.id - Watchlist ID
 * @param root0.name - Watchlist name
 * @param root0.playerCount - Player count
 * @param root0.isChecked - Whether checked
 * @param root0.onToggle - Toggle handler
 * @param root0.onShare - Share handler
 * @param root0.onEdit - Edit handler
 * @param root0.onDelete - Delete handler
 */
const SelectListRowView = ({
  id,
  name,
  playerCount,
  isChecked,
  onToggle,
  onShare,
  onEdit,
  onDelete,
}: SelectListRowViewProps) => (
  <Stack direction="horizontal" className="items-center justify-between">
    {/* SE-7739: the row-toggle Pressable covers ONLY the checkbox half. The
     * action icons below are its SIBLING, never its descendant — a nested
     * Pressable loses the native touch-responder negotiation to the enclosing
     * row Pressable, which is why the icons were dead on device. Keep them
     * siblings; do not re-wrap this Stack in the row Pressable. */}
    <Pressable
      onPress={onToggle}
      accessibilityRole="checkbox"
      accessibilityLabel={name}
      accessibilityState={{ checked: isChecked }}
      className="min-w-0 flex-1 py-3"
    >
      <Stack direction="horizontal" className="min-w-0 items-center" space="3">
        {/* SE-5432: the gluestack Checkbox root is a Pressable on native, so
         * without pointer-events-none it swallows taps (it has no onChange —
         * the row Pressable above is the single toggle target, SE-5432's
         * predecessor removed onChange to stop double-toggling on web). The
         * checkbox is purely visual; all taps must fall through to the row.
         *
         * SE-8306: being purely visual, it must also be invisible to assistive
         * technology. That same Pressable root carries its own `checkbox` role
         * and its CheckboxLabel supplies the same name as the row, so while it
         * was exposed ONE row published TWO identically-named checkboxes —
         * measured at 2, not inferred. The row Pressable above is the single
         * semantic control. The three props are not redundant: iOS, Android
         * and react-native-web each read a different one. */}
        <Checkbox
          value={id}
          size="md"
          isChecked={isChecked}
          className="pointer-events-none"
          aria-hidden
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        >
          <CheckboxIndicator>
            <CheckboxIcon as={CheckIcon} />
          </CheckboxIndicator>
          <CheckboxLabel numberOfLines={1}>{name}</CheckboxLabel>
        </Checkbox>
      </Stack>
    </Pressable>
    <Stack
      direction="horizontal"
      className="shrink-0 items-center py-3"
      space="2"
    >
      {onShare ? (
        <Pressable
          onPress={onShare}
          testID={`select-list-row-share-${id}`}
          accessibilityRole="button"
          accessibilityLabel={`Share ${name}`}
          className="size-8 items-center justify-center rounded-none"
        >
          <Icon as={UserPlusIcon} size="sm" className="text-content-subtle" />
        </Pressable>
      ) : null}
      {onEdit ? (
        <Pressable
          onPress={onEdit}
          testID={`select-list-row-edit-${id}`}
          accessibilityRole="button"
          accessibilityLabel={`Rename ${name}`}
          className="size-8 items-center justify-center rounded-none"
        >
          <Icon as={PencilIcon} size="sm" className="text-content-subtle" />
        </Pressable>
      ) : null}
      {onDelete ? (
        <Pressable
          onPress={onDelete}
          testID={`select-list-row-delete-${id}`}
          accessibilityRole="button"
          accessibilityLabel={`Delete ${name}`}
          className="size-8 items-center justify-center rounded-none"
        >
          <Icon
            as={Trash2Icon}
            size="sm"
            className="text-needs-semantic-error-400"
          />
        </Pressable>
      ) : null}
      <Text variant="caption" className="text-content-subtle">
        {playerCount}
      </Text>
    </Stack>
  </Stack>
);

SelectListRowView.displayName = "SelectListRowView";

export default memo(SelectListRowView);
